import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import { Formik, FormikProps, useFormikContext } from "formik";
import { Checkbox, Form, Input, Select, SubmitButton } from "formik-antd";
import { Col, Row } from "antd";
import { convertStringToBoolean } from "/app/src/helpers";
import DataHook from "./dataHook";
import ReportColumn from "./fields/dynamicFields/reportColumn";
import {
  IconToolTip,
  IconBuilder,
} from "/app/src/components/icons/IconBuilder";
import { genericSettingsSchema } from "/app/src/schemas/apps/orderBuilder/genericSettingsSchema";
import Box from "/app/src/components/generic/components/box";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  Button,
} from "@heroui/react";
import RedButton from "/app/src/components/HeroUi/RedButton";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
interface AutomaticSettingsFormValues {
  number?: string | number;
  token?: string;
  reportId?: number;
  values?: (string | number)[];
  value?: boolean;
  name?: string;
  creationType?: "line" | "order";
}

/**
 * ValuesValidator is a React component that validates the "values" field
 * in a form using Formik's context. It triggers validation and marks the
 * field as touched whenever the `values` prop changes.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {any} props.values - The values to be validated.
 * @returns {null} This component does not render anything.
 */
function ValuesValidator({ values }) {
  const { validateField, setFieldTouched } =
    useFormikContext<AutomaticSettingsFormValues>();
  useEffect(() => {
    validateField("values");
    setFieldTouched("values", true);
  }, [setFieldTouched, validateField, values]);
  return null;
}

/**
 *
 * @param param0 integration
 * @returns General settings form
 */
export default function GeneralSettings({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    reports,
    internalMappingSettingValue,
    creationMode,
    autoSendSettingValue,
    hasCustomStatuses,
    deleteAllFields,
    resetInternalMapping,
  } = DataHook(integration);
  const navigate = useNavigate();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [pendingValues, setPendingValues] =
    useState<AutomaticSettingsFormValues | null>(null);

  const goToReport = useCallback(() => {
    // navigate to the order page
    navigate(`/reports/${integration.reportId}`);
  }, [navigate, integration.reportId]);

  const initialValues = useMemo(() => {
    if (!internalMappingSettingValue) {
      return undefined;
    }
    return internalMappingSettingValue.split(";").map((item) => {
      const parsedInt = parseInt(item, 10);
      return isNaN(parsedInt) ? item : parsedInt;
    });
  }, [internalMappingSettingValue]);

  const contentLabel = useMemo(
    () => (
      <>
        {t("translation:auto_send_order")}
        <IconToolTip
          content={t("translation:auto_send_order_description")}
          className="ml-1"
        />
      </>
    ),
    [t],
  );
  const creationTypeLabel = useMemo(
    () => (
      <>
        {t("translation:order_builder_creation_type")}
        <IconToolTip
          content={t("translation:order_builder_type_tip")}
          className="ml-1"
        />
      </>
    ),
    [t],
  );

  const handleConfirmReportChange = useCallback(async () => {
    if (pendingValues) {
      try {
        // First, delete all fields (more destructive operation should happen first
        // so that if it fails, we haven't reset the mapping yet)
        await deleteAllFields();

        // Then reset internal name mapping
        await resetInternalMapping();

        // Finally, submit the form with the new report
        await handleSubmit(pendingValues);

        setPendingValues(null);
        onOpenChange();
        toast.success(t("translation:report_updated_successfully"));
      } catch (error) {
        // Error handling - keep modal open if operations fail
        toast.error(
          error?.message ||
            t("translation:failed_to_update_report_and_reset_fields"),
        );
        // Keep modal open so user can try again or cancel
      }
    } else {
      onOpenChange();
    }
  }, [
    pendingValues,
    handleSubmit,
    onOpenChange,
    resetInternalMapping,
    deleteAllFields,
    t,
  ]);

  const handleFormSubmit = useCallback(
    async (values: AutomaticSettingsFormValues) => {
      // Check if reportId has changed
      if (values.reportId !== integration.reportId) {
        // Store the pending values and show warning modal
        setPendingValues(values);
        onOpen();
      } else {
        // No report change, submit normally
        await handleSubmit(values);
      }
    },
    [integration.reportId, handleSubmit, onOpen],
  );

  const generalSettingsForm: (
    props: FormikProps<AutomaticSettingsFormValues>,
  ) => JSX.Element = useCallback(
    ({ dirty, isSubmitting, values }) => (
      <Form layout="vertical">
        <ValuesValidator values={values} />
        <Row className="pb-3">
          <Col className="text-lg" span={8}>
            {t("translation:general_settings")}
          </Col>
          <Col span={8} offset={8}>
            <Row justify="end">
              <SubmitButton
                disabled={!dirty || isSubmitting}
                loading={isSubmitting}
                type="primary"
              >
                {t("translation:save")}
              </SubmitButton>
            </Row>
          </Col>
        </Row>
        <Row justify="start" gutter={16}>
          <Col span={6}>
            <Form.Item
              name="name"
              label={t("translation:integration_name")}
              hasFeedback={false}
            >
              <Input
                name="name"
                placeholder={t("translation:enter_name")}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="number"
              label={creationTypeLabel}
              hasFeedback={false}
            >
              <Select disabled name="number" size="large">
                <Select.Option value="manual">
                  {t("translation:manual")}
                </Select.Option>
                <Select.Option value="automatic">
                  {t("translation:automatic")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="token"
              label={t("translation:order_creation_type")}
              hasFeedback={false}
            >
              <Select name="token" size="large">
                <Select.Option value="Pick">
                  {t("translation:pick")}
                </Select.Option>
                <Select.Option value="Put">
                  {t("translation:put")}
                </Select.Option>
                <Select.Option value="Count">
                  {t("translation:count")}
                </Select.Option>
                <Select.Option value="Replenishment">
                  {t("translation:replenishment")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="reportId"
              label={t("translation:report_build_form")}
              hasFeedback={false}
            >
              <div style={{ position: "relative" }}>
                <Select
                  name="reportId"
                  placeholder={t("translation:select_report")}
                  size="large"
                >
                  {reports?.map((report) => (
                    <Select.Option key={report.id} value={report.id}>
                      {report.name}
                    </Select.Option>
                  ))}
                </Select>
                <button
                  type="button"
                  onClick={goToReport}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-0 p-0 m-0 cursor-pointer flex items-center"
                >
                  <IconBuilder icon="LinkExternal" />
                </button>
              </div>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="creationMode"
              label={t("translation:creation_mode")}
              hasFeedback={false}
            >
              <Select name="creationMode" size="large">
                <Select.Option value="line">
                  {t("translation:line")}
                </Select.Option>
                <Select.Option value="order">
                  {t("translation:order")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <ReportColumn
              integration={integration}
              dynamic
              allowCurrentTime
              label={t("translation:internal_name_mapping")}
              size="large"
            />
          </Col>
          <Col span={8}>
            <Form.Item name="value" label={contentLabel} hasFeedback={false}>
              <Checkbox disabled={!hasCustomStatuses} name="value" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    ),
    [
      t,
      creationTypeLabel,
      integration,
      goToReport,
      reports,
      contentLabel,
      hasCustomStatuses,
    ],
  );
  const getAutoSendValue = useCallback(() => {
    if (autoSendSettingValue !== undefined) {
      return convertStringToBoolean(autoSendSettingValue);
    }
    return !hasCustomStatuses;
  }, [autoSendSettingValue, hasCustomStatuses]);

  const getInitialValues = useCallback<
    () => AutomaticSettingsFormValues
  >(() => {
    return {
      number: String(integration.number),
      token: integration.token,
      reportId: integration.reportId,
      creationMode: creationMode || "line",
      name: integration.name,
      values: initialValues,
      value: getAutoSendValue(),
    };
  }, [
    integration.number,
    integration.token,
    integration.reportId,
    integration.name,
    creationMode,
    initialValues,
    getAutoSendValue,
  ]);

  return (
    <Box>
      <Formik
        component={generalSettingsForm}
        initialValues={getInitialValues()}
        onSubmit={handleFormSubmit}
        enableReinitialize
        validationSchema={genericSettingsSchema}
      />
      <Modal
        size="md"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="top"
        aria-label="Report change warning"
      >
        <ModalContent>
          <>
            <ModalHeader>
              <div className="flex items-center">
                <IconBuilder
                  icon="ExclamationCircle"
                  color="#faad14"
                  size={32}
                  className="pr-2"
                />
                {t("translation:warning")}
              </div>
            </ModalHeader>
            <ModalBody>
              <div>
                <p>{t("translation:changing_report_warning_message")}</p>
                <ul className="list-disc ml-6 mt-2">
                  <li>{t("translation:internal_name_mapping")}</li>
                  <li>{t("translation:all_mapped_order_and_line_fields")}</li>
                </ul>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                size="sm"
                radius="none"
                color="default"
                variant="bordered"
                onPress={onOpenChange}
                className="hover:border-primary-default hover:text-primary-default"
              >
                {t("translation:cancel")}
              </Button>
              <RedButton
                size="sm"
                radius="none"
                onPress={handleConfirmReportChange}
              >
                {t("translation:continue")}
              </RedButton>
            </ModalFooter>
          </>
        </ModalContent>
      </Modal>
    </Box>
  );
}
